/**
 * Envelope encryption. Content is AES-256-GCM encrypted with a random DEK; the DEK is
 * wrapped with the workspace key (AES-KW, RFC 3394 — same as Go `aesKeyWrap`).
 *
 * Wire format of encrypted content: nonce(12) || ciphertext || tag(16), the concatenation
 * of Go `EncryptAESGCM`'s (ciphertext, nonce) outputs (backend/pkg/personalspace/crypto.go,
 * backend/pkg/crypto/aes_gcm.go).
 */
import {
  GCM_NONCE_BYTES,
  GCM_TAG_BYTES,
  b64ToBytes,
  bs,
  bytesToB64,
  bytesToHex,
  concatBytes,
} from "./bytes";

export interface Envelope {
  keyVersion: number;
  /** AES-KW(WK, DEK): 40 bytes. */
  wrappedDek: Uint8Array;
  /** nonce || ct || tag */
  ciphertext: Uint8Array;
}

/**
 * Random 256-bit DEK. It is extractable ONLY because WebCrypto cannot wrap a
 * non-extractable key; use {@link unwrapDek} to get a non-extractable copy for reads.
 */
export function generateDek(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, [
    "encrypt",
    "decrypt",
  ]);
}

export async function wrapDek(
  dek: CryptoKey,
  wk: CryptoKey
): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.wrapKey("raw", dek, wk, "AES-KW"));
}

/** Unwrap into a NON-extractable AES-GCM key. Throws if wk is wrong / data corrupt. */
export function unwrapDek(
  wrapped: Uint8Array,
  wk: CryptoKey
): Promise<CryptoKey> {
  return crypto.subtle.unwrapKey(
    "raw",
    bs(wrapped),
    wk,
    "AES-KW",
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

export async function encryptContent(
  plaintext: Uint8Array,
  dek: CryptoKey,
  aad?: Uint8Array
): Promise<Uint8Array> {
  const nonce = crypto.getRandomValues(new Uint8Array(GCM_NONCE_BYTES));
  const params: AesGcmParams = { name: "AES-GCM", iv: bs(nonce) };
  if (aad) params.additionalData = bs(aad);
  const ct = new Uint8Array(
    await crypto.subtle.encrypt(params, dek, bs(plaintext))
  );
  return concatBytes(nonce, ct);
}

export async function decryptContent(
  data: Uint8Array,
  dek: CryptoKey,
  aad?: Uint8Array
): Promise<Uint8Array> {
  if (data.length < GCM_NONCE_BYTES + GCM_TAG_BYTES) {
    throw new Error("vfs-crypto: ciphertext too short");
  }
  const params: AesGcmParams = {
    name: "AES-GCM",
    iv: bs(data.subarray(0, GCM_NONCE_BYTES)),
  };
  if (aad) params.additionalData = bs(aad);
  return new Uint8Array(
    await crypto.subtle.decrypt(params, dek, bs(data.subarray(GCM_NONCE_BYTES)))
  );
}

/** Encrypt with a fresh DEK and wrap it with the workspace key. */
export async function sealEnvelope(
  plaintext: Uint8Array,
  wk: CryptoKey,
  keyVersion = 1
): Promise<Envelope> {
  const dek = await generateDek();
  const [ciphertext, wrappedDek] = await Promise.all([
    encryptContent(plaintext, dek),
    wrapDek(dek, wk),
  ]);
  return { keyVersion, wrappedDek, ciphertext };
}

export async function openEnvelope(
  env: Envelope,
  wk: CryptoKey
): Promise<Uint8Array> {
  const dek = await unwrapDek(env.wrappedDek, wk);
  return decryptContent(env.ciphertext, dek);
}

/**
 * Re-wrap a DEK from the old workspace key to a new one (key rotation). The DEK only lives
 * in an extractable CryptoKey for the duration of this call; content is never touched.
 */
export async function rewrapDek(
  wrapped: Uint8Array,
  oldWk: CryptoKey,
  newWk: CryptoKey
): Promise<Uint8Array> {
  const dek = await crypto.subtle.unwrapKey(
    "raw",
    bs(wrapped),
    oldWk,
    "AES-KW",
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
  return wrapDek(dek, newWk);
}

/** The server-side `X-VFS-*` representation of an {@link Envelope}. */
export interface EnvelopeWire {
  wrappedDek: string;
  dekNonce: string;
  keyVersion: number;
  ciphertextSha256: string;
}

/**
 * Wire form. AES-KW has no nonce of its own, so `dekNonce` carries the 12-byte AES-GCM
 * nonce that is also the prefix of `ciphertext` (the backend only requires non-empty
 * base64; it never interprets it).
 */
export async function envelopeToWire(env: Envelope): Promise<EnvelopeWire> {
  const sha = new Uint8Array(
    await crypto.subtle.digest("SHA-256", bs(env.ciphertext))
  );
  return {
    wrappedDek: bytesToB64(env.wrappedDek),
    dekNonce: bytesToB64(env.ciphertext.subarray(0, GCM_NONCE_BYTES)),
    keyVersion: env.keyVersion,
    ciphertextSha256: bytesToHex(sha),
  };
}

export function wireToEnvelope(
  wire: { wrappedDek: string; keyVersion: number },
  ciphertext: Uint8Array
): Envelope {
  return {
    keyVersion: wire.keyVersion,
    wrappedDek: b64ToBytes(wire.wrappedDek),
    ciphertext,
  };
}
